import { MessageCircle } from "lucide-react";
import { SITE, whatsappUrl, DEFAULT_WA_MESSAGES } from "@/config/site";

/**
 * Two exports in one module:
 *   <WhatsAppFloat />  — the persistent floating bottom-right button (public pages only)
 *   <WhatsAppCTA  />   — an inline contextual CTA button (variants: primary | ghost | soft)
 *
 * Both open wa.me in a new tab with a URL-encoded pre-filled message.
 */

const WA_GREEN = "#25D366";

/* ---------- Floating persistent button ---------- */
export function WhatsAppFloat() {
  const href = whatsappUrl(DEFAULT_WA_MESSAGES.general);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Contact ${SITE.brand} on WhatsApp`}
      data-testid="wa-float"
      className="wa-float group fixed bottom-6 right-6 z-40 flex items-center justify-center rounded-full shadow-2xl transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--fl-orange)]"
      style={{
        width: 60,
        height: 60,
        background: WA_GREEN,
        boxShadow: "0 12px 28px rgba(37,211,102,0.42)",
      }}
    >
      {/* WhatsApp glyph via CDN — falls back to lucide MessageCircle */}
      <WhatsAppGlyph size={30} color="#fff" />
      {/* Hover / focus tooltip */}
      <span
        role="tooltip"
        className="pointer-events-none absolute right-[72px] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        style={{ background: "var(--fl-bg-2)", color: "var(--fl-text)", border: "1px solid var(--fl-border)" }}
      >
        Chat with us on WhatsApp
      </span>
    </a>
  );
}

/* ---------- Inline contextual CTA ---------- */
export function WhatsAppCTA({
  label,                 // fallback if labelKey / children not passed
  message,               // pre-filled msg
  variant = "primary",   // primary | ghost | soft
  size = "md",           // sm | md | lg
  className = "",
  children,
  testid = "wa-cta",
  fullWidth = false,
  onClick,               // optional side-effect fired on click (analytics, timeline logging)
}) {
  const href = whatsappUrl(message);
  const base = "inline-flex items-center gap-2 rounded-md font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--fl-orange)]";
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-6 py-3 text-base" };
  const variants = {
    primary: "text-white hover:brightness-95",
    ghost: "hover:bg-[color:var(--fl-bg-2)] border",
    soft: "hover:brightness-95",
  };
  const styles = {
    primary: { background: WA_GREEN, boxShadow: "0 6px 18px rgba(37,211,102,0.32)" },
    ghost:   { color: WA_GREEN, borderColor: WA_GREEN },
    soft:    { background: `${WA_GREEN}1a`, color: WA_GREEN, border: `1px solid ${WA_GREEN}55` },
  };
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      aria-label={label || "Contact Functional LAB on WhatsApp"}
      data-testid={testid}
      className={`${base} ${sizes[size]} ${variants[variant]} ${fullWidth ? "w-full justify-center" : ""} ${className}`}
      style={styles[variant]}
    >
      <WhatsAppGlyph size={16} />
      {children || label || "Chat on WhatsApp"}
    </a>
  );
}

/* ---------- Small SVG glyph ---------- */
export function WhatsAppGlyph({ size = 20, color = "currentColor" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill={color}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M19.11 17.5c-.28-.14-1.66-.82-1.91-.91-.26-.09-.44-.14-.63.14-.19.28-.72.9-.88 1.08-.16.18-.32.21-.6.07-.28-.14-1.18-.44-2.24-1.39-.83-.74-1.39-1.66-1.55-1.94-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.49.14-.17.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.86-2.08-.23-.55-.47-.48-.63-.49l-.54-.01c-.19 0-.49.07-.75.35-.26.28-.99.97-.99 2.37 0 1.4 1.02 2.75 1.16 2.94.14.19 2 3.04 4.85 4.26.68.29 1.2.46 1.61.59.68.22 1.3.19 1.78.11.54-.08 1.66-.68 1.89-1.34.23-.66.23-1.23.16-1.34-.07-.11-.26-.18-.54-.32zM16.04 3.2c-7.03 0-12.75 5.71-12.75 12.74 0 2.24.58 4.42 1.7 6.35L3 29l6.86-1.8a12.68 12.68 0 0 0 6.18 1.58h.01c7.03 0 12.75-5.71 12.75-12.74 0-3.4-1.32-6.6-3.73-9.01a12.68 12.68 0 0 0-9.03-3.83zm7.55 20.29c-.32.9-1.87 1.73-2.6 1.84-.66.1-1.5.14-2.42-.15-.55-.17-1.26-.4-2.17-.79-3.81-1.64-6.29-5.47-6.48-5.72-.19-.25-1.55-2.06-1.55-3.93 0-1.87.98-2.79 1.33-3.17.35-.38.76-.47 1.02-.47.26 0 .51.01.73.02.24.01.55-.09.86.66.31.75 1.06 2.6 1.15 2.79.09.19.15.4.03.65-.12.25-.17.4-.34.61-.17.21-.36.47-.51.63-.17.17-.35.36-.15.7.19.35.86 1.42 1.85 2.3 1.27 1.13 2.34 1.48 2.68 1.65.34.17.54.14.74-.09.19-.23.85-.99 1.08-1.34.23-.34.46-.28.77-.17.31.11 1.99.94 2.33 1.11.34.17.57.25.66.4.09.14.09.85-.24 1.66z" />
    </svg>
  );
}

export default WhatsAppCTA;
